import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, Alert, ActivityIndicator, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { WorkStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { CONTRACT_CATEGORIES } from '../../data/jobCategories';
import { INDIAN_STATES, getDistrictsForState } from '../../data/indianDistricts';
import ThemedText from '../../components/common/ThemedText';
import { CustomPicker } from '../../components/common/CustomPicker';
import { DatePicker } from '../../components/common/DatePicker';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

const PROJECT_LEVELS = ['Beginner', 'Medium', 'Expert'];
const DURATIONS = [
  'Less than a week',
  'Less than a month',
  '1-3 months',
  '3-6 months',
  'More than 6 months'
];
const CONTRACT_TYPES = [
  'Agency Contractors',
  'Independent Contractors',
  'New Rising Talent'
];
const LOCATION_TYPES = ['Remote', 'Hybrid', 'Onsite'];

type AddContractNavigationProp = StackNavigationProp<WorkStackParamList, 'AddContract'>;

const AddContractScreen: React.FC = () => {
  const navigation = useNavigation<AddContractNavigationProp>();
  const { theme } = useTheme();
  const { createJob } = useWork();
  const insets = useSafeAreaInsets();
  
  // Basic fields
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  
  // New fields
  const [projectLevel, setProjectLevel] = useState('');
  const [duration, setDuration] = useState('');
  const [contractType, setContractType] = useState('');
  const [locationType, setLocationType] = useState('');
  const [workAddress, setWorkAddress] = useState('');
  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [projectExpiryDate, setProjectExpiryDate] = useState<Date | undefined>();
  const [projectDeadlineDate, setProjectDeadlineDate] = useState<Date | undefined>();
  
  // PDF attachment
  const [pdfAttachment, setPdfAttachment] = useState<{
    uri: string;
    name: string;
    size: number;
  } | null>(null);
  
  const [errors, setErrors] = useState({ title: false, category: false, description: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(60);
  const { keyboardHeight } = useKeyboardHeight();

  // Get districts based on selected state
  const availableDistricts = state ? getDistrictsForState(state) : [];

  const handlePostContract = async () => {
    setErrors({ title: false, category: false, description: false });

    const newErrors = {
      title: !title.trim(),
      category: !category.trim(),
      description: !description.trim(),
    };

    setErrors(newErrors);

    if (newErrors.title || newErrors.category || newErrors.description) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createJob({
        type: 'contract',
        title: title.trim(),
        category,
        description: description.trim(),
        budget: budget ? parseFloat(budget.replace(/[^0-9.]/g, '')) : undefined,
        projectLevel: projectLevel || undefined,
        duration: duration || undefined,
        contractType: contractType || undefined,
        locationType: locationType || undefined,
        workAddress: (locationType !== 'Remote' && workAddress) ? workAddress.trim() : undefined,
        state: state || undefined,
        district: district || undefined,
        experienceYears: experienceYears ? parseInt(experienceYears) : undefined,
        projectExpiryDate: projectExpiryDate?.toISOString(),
        projectDeadlineDate: projectDeadlineDate?.toISOString(),
      } as any, pdfAttachment || undefined);

      showSuccessToast('Contract posted successfully!');
      navigation.goBack();
    } catch (error) {
      showErrorToast('Failed to post contract. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const handlePickPDF = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        const file = result.assets[0];
        setPdfAttachment({
          uri: file.uri,
          name: file.name,
          size: file.size || 0,
        });
      }
    } catch (error) {
      console.error('Error picking PDF:', error);
      showErrorToast('Failed to pick PDF file');
    }
  };

  const handleRemovePDF = () => {
    setPdfAttachment(null);
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={Platform.OS === 'ios' ? ['left', 'right'] : ['top', 'left', 'right']}>
      <View 
        style={[styles.header, { borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Post a Contract</ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView 
        style={styles.content} 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Basic Information Section */}
        <View style={styles.sectionHeader}>
          <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Basic Information</ThemedText>
          <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Required fields for your contract</ThemedText>
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Contract Title *</ThemedText>
          <TextInput
            style={[
              styles.input, 
              { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
              errors.title && { borderWidth: 2, borderColor: theme.error }
            ]}
            placeholder="e.g., Construction of House"
            value={title}
            onChangeText={(text) => {
              setTitle(text);
              if (errors.title) setErrors({ ...errors, title: false });
            }}
            placeholderTextColor={theme.textSecondary}
          />
          {errors.title && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Category *</ThemedText>
          <CustomPicker
            selectedValue={category || undefined}
            onValueChange={(value) => {
              setCategory(value || '');
              if (errors.category) setErrors({ ...errors, category: false });
            }}
            items={[
              { label: 'Select a category', value: undefined },
              ...CONTRACT_CATEGORIES.map(cat => ({ label: cat, value: cat }))
            ]}
            placeholder="Select a category"
            searchable={true}
            searchPlaceholder="Search categories..."
          />
          {errors.category && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Description *</ThemedText>
          <TextInput
            style={[
              styles.input, 
              styles.textArea, 
              { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
              errors.description && { borderWidth: 2, borderColor: theme.error }
            ]}
            placeholder="Describe what work you need done..."
            value={description}
            onChangeText={(text) => {
              setDescription(text);
              if (errors.description) setErrors({ ...errors, description: false });
            }}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
            placeholderTextColor={theme.textSecondary}
          />
          {errors.description && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        {/* Project Details Section */}
        <View style={styles.sectionHeader}>
          <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Project Details</ThemedText>
          <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Additional information to help find the right match</ThemedText>
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Budget (Optional)</ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }]}
            placeholder="e.g., 5000"
            value={budget}
            onChangeText={setBudget}
            keyboardType="numeric"
            placeholderTextColor={theme.textSecondary}
          />
          <ThemedText style={[styles.helperText, { color: theme.textSecondary }]}>
            Others will send you quotes for this work
          </ThemedText>
        </View>

        {/* Project Level */}
        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Project Level (Optional)</ThemedText>
          <CustomPicker
            selectedValue={projectLevel || undefined}
            onValueChange={(value) => setProjectLevel(value || '')}
            items={[
              { label: 'Select project level', value: undefined },
              ...PROJECT_LEVELS.map(level => ({ label: level, value: level }))
            ]}
            placeholder="Select project level"
          />
        </View>

        {/* Duration */}
        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Duration (Optional)</ThemedText>
          <CustomPicker
            selectedValue={duration || undefined}
            onValueChange={(value) => setDuration(value || '')}
            items={[
              { label: 'Select duration', value: undefined },
              ...DURATIONS.map(dur => ({ label: dur, value: dur }))
            ]}
            placeholder="Select duration"
          />
        </View>

        {/* Contract Type */}
        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Contract Type (Optional)</ThemedText>
          <CustomPicker
            selectedValue={contractType || undefined}
            onValueChange={(value) => setContractType(value || '')}
            items={[
              { label: 'Select contract type', value: undefined },
              ...CONTRACT_TYPES.map(type => ({ label: type, value: type }))
            ]}
            placeholder="Select contract type"
          />
        </View>

        {/* Location & Timeline Section */}
        <View style={styles.sectionHeader}>
          <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Location & Timeline</ThemedText>
          <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Set work location and project dates</ThemedText>
        </View>

        {/* Location Type */}
        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Location Type (Optional)</ThemedText>
          <CustomPicker
            selectedValue={locationType || undefined}
            onValueChange={(value) => setLocationType(value || '')}
            items={[
              { label: 'Select location type', value: undefined },
              ...LOCATION_TYPES.map(type => ({ label: type, value: type }))
            ]}
            placeholder="Select location type"
          />
        </View>

        {/* Work Address - Only show if not Remote */}
        {locationType && locationType !== 'Remote' && (
          <>
            <View style={styles.formGroup}>
              <ThemedText style={[styles.label, { color: theme.text }]}>State</ThemedText>
              <CustomPicker
                selectedValue={state || undefined}
                onValueChange={(value) => {
                  setState(value || '');
                  setDistrict(''); // Reset district when state changes
                }}
                items={[
                  { label: 'Select state', value: undefined },
                  ...INDIAN_STATES.map(s => ({ label: s, value: s }))
                ]}
                placeholder="Select state"
                searchable={true}
                searchPlaceholder="Search states..."
              />
            </View>

            {state && (
              <View style={styles.formGroup}>
                <ThemedText style={[styles.label, { color: theme.text }]}>District</ThemedText>
                <CustomPicker
                  selectedValue={district || undefined}
                  onValueChange={(value) => setDistrict(value || '')}
                  items={[
                    { label: 'Select district', value: undefined },
                    ...availableDistricts.map(d => ({ label: d, value: d }))
                  ]}
                  placeholder="Select district"
                  searchable={true}
                  searchPlaceholder="Search districts..."
                />
              </View>
            )}

            <View style={styles.formGroup}>
              <ThemedText style={[styles.label, { color: theme.text }]}>Work Address</ThemedText>
              <TextInput
                style={[
                  styles.input,
                  styles.textArea,
                  { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }
                ]}
                placeholder="Enter detailed work location address"
                value={workAddress}
                onChangeText={setWorkAddress}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          </>
        )}

        {/* Years of Experience */}
        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Years of Experience Preferred (Optional)</ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border }]}
            placeholder="e.g., 5"
            value={experienceYears}
            onChangeText={setExperienceYears}
            keyboardType="numeric"
            placeholderTextColor={theme.textSecondary}
          />
        </View>

        {/* Project Expiry Date */}
        <DatePicker
          value={projectExpiryDate}
          onChange={setProjectExpiryDate}
          label="Project Expiry Date (Optional)"
          helperText="When this listing should expire"
          minimumDate={new Date()}
        />

        {/* Project Deadline Date */}
        <DatePicker
          value={projectDeadlineDate}
          onChange={setProjectDeadlineDate}
          label="Project Deadline Date (Optional)"
          helperText="When the project needs to be completed"
          minimumDate={new Date()}
        />

        {/* PDF Attachment Section */}
        <View style={styles.sectionHeader}>
          <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Contract Document</ThemedText>
          <ThemedText style={[styles.sectionSubtitle, { color: theme.textSecondary }]}>Attach PDF contract (optional)</ThemedText>
        </View>

        <View style={styles.formGroup}>
          {!pdfAttachment ? (
            <TouchableOpacity
              style={[styles.pdfPickerButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
              onPress={handlePickPDF}
            >
              <Ionicons name="document-attach-outline" size={32} color={theme.accent} />
              <ThemedText style={[styles.pdfPickerText, { color: theme.text }]}>Upload PDF Document</ThemedText>
              <ThemedText style={[styles.pdfPickerSubtext, { color: theme.textSecondary }]}>Max size: 10MB</ThemedText>
            </TouchableOpacity>
          ) : (
            <View style={[styles.pdfAttachmentCard, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}>
              <View style={styles.pdfAttachmentInfo}>
                <Ionicons name="document-text" size={40} color={theme.accent} />
                <View style={styles.pdfAttachmentDetails}>
                  <ThemedText style={[styles.pdfFileName, { color: theme.text }]} numberOfLines={1}>
                    {pdfAttachment.name}
                  </ThemedText>
                  <ThemedText style={[styles.pdfFileSize, { color: theme.textSecondary }]}>
                    {(pdfAttachment.size / 1024 / 1024).toFixed(2)} MB
                  </ThemedText>
                </View>
              </View>
              <TouchableOpacity onPress={handleRemovePDF} style={styles.pdfRemoveButton}>
                <Ionicons name="close-circle" size={28} color={theme.error} />
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity style={[styles.cancelButton, { borderColor: theme.border }]} onPress={handleCancel} disabled={isSubmitting}>
            <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>Cancel</ThemedText>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.postButton, { backgroundColor: theme.accent, opacity: isSubmitting ? 0.6 : 1 }]}
            onPress={handlePostContract}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <ActivityIndicator color={theme.textInverse} />
            ) : (
              <ThemedText style={[styles.postButtonText, { color: theme.textInverse }]}>Post Contract</ThemedText>
            )}
          </TouchableOpacity>
        </View>

        <FormFooterSpacer
          headerHeight={headerHeight}
          bottomInset={insets.bottom}
          keyboardHeight={keyboardHeight}
        />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 24,
  },
  sectionHeader: {
    marginTop: 8,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 13,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    borderWidth: 1,
  },
  pickerInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
  },
  pickerText: {
    fontSize: 16,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  postButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  postButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 4,
  },
  clearButton: {
    padding: 4,
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
  },
  clearDateButton: {
    marginTop: 8,
  },
  clearDateText: {
    fontSize: 14,
    fontWeight: '600',
  },
  datePickerDone: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  datePickerDoneText: {
    fontSize: 14,
    fontWeight: '600',
  },
  categoryOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  categoryOptionText: {
    fontSize: 16,
  },
  pdfPickerButton: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfPickerText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
  },
  pdfPickerSubtext: {
    fontSize: 14,
    marginTop: 4,
  },
  pdfAttachmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  pdfAttachmentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  pdfAttachmentDetails: {
    flex: 1,
    marginLeft: 12,
  },
  pdfFileName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  pdfFileSize: {
    fontSize: 14,
  },
  pdfRemoveButton: {
    padding: 4,
  },
});

export default AddContractScreen;
